import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MapView from '../components/MapView';
import { api } from '../utils/api';
import LoginModal from '../components/LoginModal';
import Footer from '../components/Footer';
import AboutModal from '../components/AboutModal';
import TermsModal from '../components/TermsModal';
import PrivacyModal from '../components/PrivacyModal';
import SubscribeModal from '../components/SubscribeModal';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import './HomePage.css';

const HomePage = () => {
    const [discs, setDiscs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
    const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        loadDiscs();
    }, []);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        try {
            const user = await getCurrentUser();
            setIsLoggedIn(true);
        } catch (err) {
            setIsLoggedIn(false);
        }
    };

    const handleLoginSuccess = async () => {
        setIsLoginModalOpen(false);
        await checkAuthStatus();
    };

    const handleLogout = async () => {
        try {
            await signOut();
            setIsLoggedIn(false);
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const loadDiscs = async () => {
        try {
            setLoading(true);
            const discsData = await api.getDiscs();
            console.log('Loaded discs:', discsData);
            setDiscs(discsData);
            setError(null);
        } catch (err) {
            console.error('Error loading discs:', err);
            setError('Failed to load disc locations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkerClick = (disc) => {
        navigate(`/disc/${disc.discId}?source=map`);
    };

    return (
        <div className="home-page">
            <header className="home-header">
                <h1>Jeffrey Discs</h1>
                <p className="tagline">Celebrating Jeffrey's Spirit, One Throw at a Time</p>
            </header>

            {loading && (
                <div className="loading">
                    <p>Loading disc locations...</p>
                </div>
            )}

            {error && (
                <div className="error">
                    <p>{error}</p>
                    <button onClick={loadDiscs}>Try Again</button>
                </div>
            )}

            {!loading && !error && discs.length > 0 && (
                <div className="map-section">
                    <p className="map-instruction">Click a Disc's Pin to See Its Journey</p>
                    <div className="map-container">
                        <MapView 
                            discs={discs}
                            onMarkerClick={handleMarkerClick}
                        />
                    </div>
                    <div className="map-actions">
                        <button 
                            className="about-button"
                            onClick={() => setIsAboutModalOpen(true)}
                        >
                            About Jeffrey Discs
                        </button>
                        <button 
                            className="subscribe-button"
                            onClick={() => setIsSubscribeModalOpen(true)}
                        >
                            Get Monthly Disc Updates
                        </button>
                    </div>
                </div>
            )}

            {!loading && !error && discs.length === 0 && (
                <div className="no-discs">
                    <p>No discs have been found yet!</p>
                    <p>Be the first to scan a QR code and report a disc location.</p>
                    <div className="map-actions">
                        <button 
                            className="about-button"
                            onClick={() => setIsAboutModalOpen(true)}
                        >
                            About Jeffrey Discs
                        </button>
                        <button 
                            className="subscribe-button"
                            onClick={() => setIsSubscribeModalOpen(true)}
                        >
                            Get Monthly Disc Updates
                        </button>
                    </div>
                </div>
            )}

            <Footer 
                onTermsClick={() => setIsTermsModalOpen(true)}
                onPrivacyClick={() => setIsPrivacyModalOpen(true)}
                isLoggedIn={isLoggedIn}
                onLoginClick={() => setIsLoginModalOpen(true)}
                onLogoutClick={handleLogout}
            />

            <LoginModal 
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onLoginSuccess={handleLoginSuccess}
            />

            <AboutModal 
                isOpen={isAboutModalOpen}
                onClose={() => setIsAboutModalOpen(false)}
            />

            <TermsModal 
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
            />

            <PrivacyModal 
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
            />

            <SubscribeModal 
                isOpen={isSubscribeModalOpen}
                onClose={() => setIsSubscribeModalOpen(false)}
            />
        </div>
    );
};

export default HomePage;